import doc              from '@widesk-core/decorators/doc';
import model            from '@widesk-core/decorators/model';
import MapServiceModel  from '@/models/MapServiceModel';
import TimestampAble    from '@widesk-core/models/traits/TimestampAble';
import Blamable         from '@/models/traits/Blamable';

export type ThemeScope = 'normal' | 'hover';

/**
 * Mêmes noms que `ShapeStyle` de `@tokamap/core`, pour qu'appliquer un thème à une forme reste une
 * correspondance directe.
 *
 * Un thème porte TOUTE l'apparence d'une forme liée : le rendu remplace le style propre de la forme
 * par celui-ci (voir `applyShapeTheme`), donc un champ laissé vide ici ne retombe PAS sur la valeur
 * de la forme mais sur le défaut de l'adapter (bordure transparente, épaisseur par défaut...).
 */
export type ThemeStyle = {
	borderColor?: string;
	bgColor?: string;
	borderWidth?: number;
	borderDashed?: boolean;
};

/**
 * Couleurs du texte portées par un thème. Un thème ne porte NI le contenu du texte NI sa taille :
 * une forme sans texte n'affiche rien et le thème ne peut pas l'inventer (voir `ShapeTheme.text`
 * dans `@tokamap/core`). Comme pour `ThemeStyle`, un champ vide ne retombe pas sur la valeur de la
 * forme mais sur le défaut de l'adapter.
 */
export type ThemeTextStyle = {
	color?: string;
	bgColor?: string;
	/** Mêmes principes que `ThemeConfiguration.height`. */
	fixedSizePx?: number | null;
};

/**
 * `bgColor` et `borderColor` à la racine sont exigés par le schéma `ThemeConfiguration` de l'API, qui
 * rejette la configuration sans eux ; aucun écran ne les lit, tous passent par `appearance`. Le
 * schéma accepte toute autre clé sans en valider le contenu, d'où la normalisation de `themeStore`.
 */
export type ThemeConfiguration = {
	bgColor: string;
	borderColor: string;
	appearance: Record<ThemeScope, ThemeStyle>;
	text: Record<ThemeScope, ThemeTextStyle>;
	/** Absente ou `null` : chaque forme liée garde sa propre hauteur. `null` plutôt qu'une clé omise
	 * pour la retirer, l'API patchant en `merge-patch+json`. */
	height?: number | null;
	/** Mêmes principes que `height`. */
	elevation?: number | null;
	/** Au survol ; absente ou `null`, celle de `height`. */
	hoverHeight?: number | null;
	/** Mêmes principes que `hoverHeight`, pour `elevation`. */
	hoverElevation?: number | null;
	/** Mêmes principes que `height`, pour `Shape.minZoomOffset`. */
	minZoomOffset?: number | null;
};

@model.urnResource('theme')
@doc.path('/themes/{?id}')
export default class ThemeModel extends Blamable(TimestampAble(MapServiceModel)) {
	private declare _tsFilters: {
		'createdAt': string;
		'placeTypes': id;
		'project': id;
		'updatedAt': string | Date | Dayjs;
	};

	private declare _tsSorts: {
		'createdAt': string;
		'id': string;
		'label': string;
		'updatedAt': string;
	};

	@doc.string declare label: string;
	@doc.json declare configuration: ThemeConfiguration;
}
